import React from 'react';
import { Download, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PensionPlanTransfer } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { findArcaPlanById, findArcaPlanByName, ArcaPlan } from '../../constants/arcaPlans';
import { INDIVIDUAL_PLANS_CONTENT } from '../../data/individualPlansContent';
import toTitleCase from '../../utils/toTitleCase';
import { fmtNum } from '../../utils/formatArcaNumbers';
import { PlanReturnView, todayPct } from '../../utils/transferReturns';

interface ChosenPlanCardProps {
	transfer: PensionPlanTransfer;
	plans: ArcaPlan[];
	/**
	 * Rentabilidad de cada plan por slug, de comparsa (`/plan-returns/arca-plans`). Sin entrada
	 * o sin mapa, «sin datos de rentabilidad»: nunca una tasa de respaldo.
	 */
	planReturns: Record<string, PlanReturnView | null> | undefined;
	translate: (key: string) => string;
	onViewOthers: () => void;
}

/**
 * Card del plan elegido en solitario (Path N / traspaso sin comparativa): logo,
 * filosofía, rentabilidad y comisión, DFP y acceso a otras opciones. La comparación
 * plan actual vs elección vive en ComparisonTable (SituationAnalysis).
 */
const ChosenPlanCard: React.FC<ChosenPlanCardProps> = ({
	transfer,
	plans,
	planReturns,
	translate,
	onViewOthers
}) => {
	const planId = transfer.selectedArcaPlanId || transfer.metadata?.selectedPlanId;
	const arcaPlan =
		(planId ? findArcaPlanById(planId, plans) : undefined) ??
		findArcaPlanByName(transfer.metadata?.plan_name ?? '', plans);
	const contentKey = transfer.catalogId ?? arcaPlan?.catalogSlug;
	// `undefined` mientras comparsa responde; `null` o sin entrada = sin datos.
	const loadingReturns = planReturns === undefined;
	const r = arcaPlan ? planReturns?.[arcaPlan.catalogSlug] : undefined;
	const content = contentKey ? INDIVIDUAL_PLANS_CONTENT[contentKey] : undefined;

	if (!arcaPlan) {
		return (
			<div className="surface-soft p-4 text-sm text-muted-foreground">
				{translate('individual.exploration.chosenPlan.title') || 'Tu elección'}
			</div>
		);
	}

	return (
		<div className="surface-card p-5 space-y-4 h-full">
			{/* Header */}
			<div className="flex items-center gap-3">
				<img
					src={arcaPlan.logo}
					alt={arcaPlan.name}
					className="w-12 h-12 rounded-xl object-contain bg-white border border-border p-1 shrink-0"
				/>
				<div className="min-w-0">
					{content && (
						<span className="inline-block text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full mb-1">
							{content.philosophyTagline}
						</span>
					)}
					<p className="text-base font-semibold leading-snug">{toTitleCase(arcaPlan.name)}</p>
				</div>
			</div>

			{/* Philosophy */}
			{content && (
				<p className="text-sm text-muted-foreground leading-relaxed">{content.philosophy}</p>
			)}

			{/* Target investor */}
			{content && (
				<div className="space-y-1">
					<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
						{translate('individual.exploration.chosenPlan.targetInvestor') ||
							'¿A quién va dirigido?'}
					</p>
					<p className="text-sm">{content.targetInvestor}</p>
				</div>
			)}

			{/* Stats, de comparsa. Con 1 año de histórico se oculta la acumulada (sería igual a la
			    anualizada). Sin datos, se dice así. */}
			{(() => {
				if (loadingReturns) {
					return (
						<p className="bg-muted/50 rounded-xl p-3 text-sm text-muted-foreground">
							{translate('individual.exploration.chosenPlan.loadingReturns') ||
								'Cargando rentabilidad…'}
						</p>
					);
				}
				if (!r) {
					return (
						<p className="bg-muted/50 rounded-xl p-3 text-sm text-muted-foreground">
							{translate('individual.exploration.chosenPlan.noReturns') ||
								'Sin datos de rentabilidad'}
						</p>
					);
				}
				return (
					<div className="grid grid-cols-2 gap-3">
						<div
							className={cn(
								'bg-muted/50 rounded-xl p-3 space-y-0.5',
								r.periodYears <= 1 && 'col-span-2'
							)}
						>
							<p className="text-xs text-muted-foreground">
								{(
									translate('individual.exploration.chosenPlan.netReturnAnnual') ||
									'Rentabilidad neta anualizada {years}a'
								).replace('{years}', String(r.periodYears))}
							</p>
							<p className="text-lg font-bold text-primary">
								{todayPct(translate, r, `${fmtNum(r.annualPct)}%`)}
							</p>
						</div>
						{r.periodYears > 1 && (
							<div className="bg-muted/50 rounded-xl p-3 space-y-0.5">
								<p className="text-xs text-muted-foreground">
									{(
										translate('individual.exploration.chosenPlan.netReturnCumulative') ||
										'Rentabilidad acumulada {years}a'
									).replace('{years}', String(r.periodYears))}
								</p>
								<p className="text-lg font-bold text-primary">
									{fmtNum((Math.pow(1 + r.annualPct / 100, r.periodYears) - 1) * 100)}%
								</p>
							</div>
						)}
					</div>
				);
			})()}

			{/* Commissions */}
			<div className="space-y-1.5">
				<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
					{translate('individual.exploration.chosenPlan.fees') || 'Comisiones'}
				</p>
				<div className="flex justify-between text-sm">
					<span className="text-muted-foreground">
						{translate('individual.exploration.chosenPlan.feeManagement') || 'Gestión'}
					</span>
					<span className="font-medium">
						{r && r.managementFeePct !== null ? `${fmtNum(r.managementFeePct)}%` : '—'}
					</span>
				</div>
			</div>

			{/* DFP button */}
			{content?.dfpUrl && (
				<button
					className="w-full bg-white text-foreground font-semibold text-sm rounded-2xl px-6 py-3 flex items-center justify-center gap-2 shadow-xs hover:bg-muted/50 transition-colors"
					onClick={() => window.open(content.dfpUrl, '_blank', 'noopener,noreferrer')}
				>
					<Download className="w-4 h-4" />
					{translate('individual.exploration.chosenPlan.downloadDfp') ||
						'Descargar información oficial (DFP)'}
				</button>
			)}

			{/* View others link */}
			<button
				onClick={onViewOthers}
				className="flex items-center gap-1 text-sm text-primary hover:underline w-full justify-center"
			>
				{translate('individual.exploration.chosenPlan.viewOthers') || 'Ver otras opciones'}
				<ChevronRight className="w-4 h-4" />
			</button>
		</div>
	);
};

export default ChosenPlanCard;
